/**
 * Barry-Wehmiller (BW) account statement -- pure data/HTML builders.
 *
 * Extracted from src/shared_assets/index.js's Account Statement subsystem (#5447
 * componentization pass). These are the BW-specific counterparts to
 * account-statement-default.js/account-statement-fund.js -- pure functions with
 * no DOM access, called from renderBWAccountStatement() (which stays in index.js
 * since it owns the loading-modal/PDF-download DOM wiring).
 *
 * clientData/client/window.lastCalculationResult/window.lastTimeSeriesResult are
 * read as bare/window globals here, matching the window-bridge pattern the rest
 * of this codebase uses (see CLAUDE.md's AppState bridge) -- they are not passed
 * as parameters because every other already-extracted helper in this directory
 * follows the same convention.
 */
import moment from 'moment';
import { getCertNumber, splitAddressFields } from './string-utils.js';
import { listTransactionCustomColumns } from './transaction-custom-column-value.js';
import { escapeStatementHtml as escapeHtml, buildStatementChartSvg } from './account-statement-shared.js';

const normalizeLabel = (s) =>
	String(s || '')
		.toLowerCase()
		.replace(/[^a-z0-9]/g, '');

/**
 * #6405: BW's "Gift FMV" custom column, found by its label (or its `gift_fmv` key), or null.
 * Was "the first custom column", which any other custom column would have been mistaken for.
 */
export function findGiftFmvColumn(clientData) {
	const cols = listTransactionCustomColumns(clientData);
	return cols.find((c) => normalizeLabel(c.label) === 'giftfmv') || cols.find((c) => c.key === 'gift_fmv') || null;
}

export function buildBWHeaderTemplate(targetUserData, logoBase64 = '') {
	/* Generates PDF header template with company name, title, and contact info */
	const userName = escapeHtml(targetUserData.user_name?.replace(/_/g, ' ') || '');
	const userAddress = targetUserData.clients?.[client]?.address || '';
	const userPhone = escapeHtml(targetUserData.phone || '');
	const userEmail = escapeHtml(targetUserData.userid || '');
	const companyName = escapeHtml(clientData.company_name || 'BARRY-WEHMILLER GROUP, INC.');

	// Split address into lines
	const rawAddressParts = splitBWAddress(userAddress);
	const addressParts = {
		line1: escapeHtml(rawAddressParts.line1),
		line2: escapeHtml(rawAddressParts.line2),
		line3: escapeHtml(rawAddressParts.line3),
	};

	// Use base64 logo if available, otherwise fall back to company name text
	const logoOrName = logoBase64 ? `<img class="company-logo" src="${logoBase64}" alt="${companyName}">` : `<span class="company-name">${companyName}</span>`;

	return `<style>
		* { margin: 0; padding: 0; box-sizing: border-box; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
		.header-container { width: 100%; font-family: Arial, sans-serif; padding: 15px 30px; font-size: 10px; }
		.company-logo { max-height: 44px; }
		.company-name { font-size: 20px; font-weight: bold; color: #243b53; }
		.statement-title { font-size: 13px; font-weight: bold; color: #243b53; margin-top: 6px; margin-bottom: 20px; }
		.contact-row { display: flex; justify-content: space-between; width: 100%; }
		.contact-left { font-size: 10px; color: #243b53; line-height: 1.4; }
		.contact-left .name { font-weight: bold; }
		.contact-right { text-align: right; font-size: 10px; color: #243b53; line-height: 1.4; }
	</style>
	<div class="header-container">
		<div style="text-align: center;">
			${logoOrName}
		</div>
		<div class="statement-title" style="text-align: center;">EQUITY HOLDER ACCOUNT STATEMENT</div>
		<div class="contact-row">
			<div class="contact-left">
				<div class="name">${userName}</div>
				${userPhone ? '<div>' + userPhone + '</div>' : ''}
				${userEmail ? '<div>' + userEmail + '</div>' : ''}
			</div>
			<div class="contact-right">
				${addressParts.line1 ? '<div>' + addressParts.line1 + '</div>' : ''}
				${addressParts.line2 ? '<div>' + addressParts.line2 + '</div>' : ''}
				${addressParts.line3 ? '<div>' + addressParts.line3 + '</div>' : ''}
			</div>
		</div>
	</div>`;
}

export function buildBWFooterTemplate() {
	/* Generates PDF footer template with company address and page numbers */
	const companyAddress = '8020 Forsyth Boulevard, St. Louis, MO  63105';
	const companyPhone = '+1 (314) 862-8000';

	return `<style>
		* { margin: 0; padding: 0; box-sizing: border-box; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
		.footer-container { width: 100%; font-family: Arial, sans-serif; font-size: 9px; padding: 8px 30px; color: #243b53; }
		.footer-inner { border-top: 1px solid #243b53; border-bottom: 1px solid #243b53; padding: 6px 0; display: flex; justify-content: space-between; align-items: center; }
		.company-text { }
		.page-num { font-size: 11px; font-weight: bold; }
	</style>
	<div class="footer-container">
		<div class="footer-inner">
			<div><span class="company-text">Barry-Wehmiller</span> Group, Inc.  |  ${companyAddress}  |  ${companyPhone}</div>
			<div class="page-num">Page <span class="pageNumber"></span> of <span class="totalPages"></span></div>
		</div>
	</div>`;
}

export function buildBWStatementData(transactions, targetUserData) {
	/* Processes transactions to build statement data structure */
	const classes = Object.keys(clientData.equity);
	const statementDate = moment().format('MM/DD/YYYY');
	const currentYear = new Date().getFullYear();

	// Get calculator breakdown for share prices (already calculated during page load)
	const calculatorBreakdown = window.lastCalculationResult?.breakdown || [];

	// Initialize data structure for each share class
	const byClass = {};
	classes.forEach((c) => {
		// Get share price from calculator result (avoids duplicate valuation lookup)
		const calcClassData = calculatorBreakdown.find((cd) => cd.classKey === c);

		byClass[c] = {
			shareClass: c,
			shareClassName: clientData.equity[c]?.name || c,
			sharesType: clientData.equity[c]?.sharesType || 'shares',
			certificated: [],
			uncertificated: [],
			totalCertificated: 0,
			totalUncertificated: 0,
			totalDRIP: 0, // Always 0 (not tracked)
			totalShares: 0,
			currentPrice: calcClassData?.sharePrice || 0,
			currentValue: 0,
			ytdDividends: calculateBWYTDDividends(c, targetUserData, currentYear),
			// Vesting data from calculator
			vestedShares: calcClassData?.vested || 0,
			totalTargetShares: calcClassData?.totalTarget || 0,
			// #6266: scheduleVested (uncapped) not vested (capped at owned), or a
			// redemption of already-vested shares re-inflates this back to totalTarget.
			unvestedShares: Math.max(0, (calcClassData?.totalTarget || 0) - (calcClassData?.scheduleVested ?? calcClassData?.vested ?? 0)),
			unvestedValue: calcClassData?.unvestedValue || 0,
		};
	});

	const giftFmvCol = findGiftFmvColumn(clientData);

	// Process each transaction
	transactions.forEach((txn) => {
		const shareClass = txn.shareClass;
		if (!byClass[shareClass]) return;

		const quantity = Number(String(txn.quantity || '').replace(/[^.\d]/g, '')) || 0;
		const certNumber = getCertNumber(txn.notes);
		// Per-share basis: Tax Basis column, else the engine's resolved/carried-forward
		// basis, else legacy strikePrice (pre-2026-10 data). Unknown stays null ("n.a."), never 0.
		const toNum = (v) => (/\d/.test(String(v ?? '')) ? Number(String(v).replace(/[^.\d]/g, '')) : null);
		const basisNums = [txn.taxBasis, txn._effectiveTaxBasis, txn.strikePrice].map(toNum);
		const costBasisPerShare = basisNums.find((n) => n > 0) ?? (basisNums[0] === 0 || basisNums[1] === 0 ? 0 : null);

		// Gift/inherited: a value in the Gift FMV column
		const giftFMV = giftFmvCol ? Number(String(txn[giftFmvCol.key] || '').replace(/[^.\d]/g, '')) || 0 : 0;
		const isGiftInherited = giftFMV > 0;

		// Check for cancelled/expiration date (expDate field in BW data)
		const cancelledDateRaw = txn.expDate || txn.cancelledDate || txn.cancelled_date || '';
		const cancelledDate = cancelledDateRaw ? moment.unix(cancelledDateRaw).format('MM/DD/YYYY') : '';

		const txnData = {
			originalAcquiredDate: moment.unix(txn.date).format('MM/DD/YYYY'),
			certificateDate: certNumber ? moment.unix(txn.date).format('MM/DD/YYYY') : '',
			certificateNumber: certNumber || '',
			shares: quantity,
			giftInherited: isGiftInherited ? 'Yes' : '',
			giftDate: txn.giftDate ? moment.unix(txn.giftDate).format('MM/DD/YYYY') : '',
			giftBasis: isGiftInherited ? giftFMV : 0,
			costBasisCumulative: costBasisPerShare == null ? null : quantity * costBasisPerShare,
			costBasisPerShare: costBasisPerShare,
			cancelledDate: cancelledDate,
		};

		if (certNumber) {
			byClass[shareClass].certificated.push(txnData);
			byClass[shareClass].totalCertificated += quantity;
		} else {
			byClass[shareClass].uncertificated.push(txnData);
			byClass[shareClass].totalUncertificated += quantity;
		}
	});

	// Calculate totals for each class and portfolio total
	let totalPortfolioValue = 0;
	let totalYtdDividends = 0;
	let totalUnvestedValue = 0;
	Object.values(byClass).forEach((classData) => {
		classData.totalShares = classData.totalCertificated + classData.totalUncertificated;
		classData.currentValue = classData.totalShares * classData.currentPrice;
		totalPortfolioValue += classData.currentValue;
		totalYtdDividends += classData.ytdDividends;
		totalUnvestedValue += classData.unvestedValue;
	});

	// Build individual unvested grants array for detailed table display
	const unvestedGrants = [];
	classes.forEach((c) => {
		const vesting = targetUserData.clients?.[client]?.shares?.[c]?.vesting;
		const calcClassData = calculatorBreakdown.find((cd) => cd.classKey === c);
		const currentPrice = calcClassData?.sharePrice || 0;
		const className = clientData.equity[c]?.name || c;

		if (vesting) {
			Object.entries(vesting).forEach(([trancheName, trancheData]) => {
				if (trancheData.cashedOut === true) return; // skip cashed-out tranches
				if (trancheData.vested === true) return; // skip fully vested tranches (BW tranche-level flag)
				// Calculate unvested amount from schedule entries where vested !== true
				let unvestedEquity = 0;
				if (trancheData.schedule) {
					Object.values(trancheData.schedule).forEach((entry) => {
						if (!entry.vested && !entry.forfeited) {
							unvestedEquity += Number(entry.amount) || 0;
						}
					});
				}

				if (unvestedEquity > 0) {
					// Parse "Original Grant Value: $XXX" from conditions
					let targetValueAtGrant = 0;
					const condMatch = trancheData.conditions?.match(/Original Grant Value:\s*\$?([\d,]+\.?\d*)/i);
					if (condMatch) targetValueAtGrant = Number(condMatch[1].replace(/,/g, ''));

					unvestedGrants.push({
						stockType: `${trancheName} ${className}`,
						trancheName: trancheName,
						equity: unvestedEquity,
						targetValueAtGrant: targetValueAtGrant,
						currentPrice: currentPrice,
						unvestedValue: unvestedEquity * currentPrice,
					});
				}
			});
		}
	});

	// Sort unvested grants: FY tranches first (oldest to newest), then others
	const isFYKey = (k) => /^FY\s*\d{2,4}\b/i.test(String(k || '').trim());
	unvestedGrants.sort((a, b) => {
		const aIsFY = isFYKey(a.trancheName);
		const bIsFY = isFYKey(b.trancheName);
		if (aIsFY && !bIsFY) return -1;
		if (!aIsFY && bIsFY) return 1;
		if (aIsFY && bIsFY) {
			const ay = Number(String(a.trancheName).match(/FY\s*(\d{2,4})/i)?.[1]) || 0;
			const by = Number(String(b.trancheName).match(/FY\s*(\d{2,4})/i)?.[1]) || 0;
			return ay - by; // Oldest first
		}
		return String(a.trancheName).localeCompare(String(b.trancheName));
	});

	// Get time series data for the chart (already built by TimeSeriesBuilder)
	const timeSeriesData = window.lastTimeSeriesResult?.data || [];

	return {
		statementDate,
		byClass,
		classes,
		totalPortfolioValue,
		totalYtdDividends,
		totalUnvestedValue,
		unvestedGrants,
		timeSeriesData,
	};
}

export function calculateBWYTDDividends(shareClass, targetUserData, currentYear) {
	/* Calculates year-to-date dividends for a share class */
	const userShares = targetUserData.clients?.[client]?.shares?.[shareClass];
	if (!userShares?.events) return 0;

	const clientEvents = clientData.equity[shareClass]?.events || [];
	const yearStart = new Date(currentYear, 0, 1).getTime() / 1000;

	let totalDividends = 0;
	userShares.events.forEach((ue) => {
		const clientEvent = clientEvents.find((ce) => ce.nrid === ue.nrid);
		if (clientEvent && Number(clientEvent.date) >= yearStart && (clientEvent.eventType === 'Distribution' || clientEvent.eventType === 'Cash Dividend')) {
			totalDividends += Number(ue.grossDollars) || 0;
		}
	});

	return totalDividends;
}

export function buildBWStatementHtml(statementData) {
	/* Generates HTML body content - header/footer handled by PDF template */
	const primaryColor = clientData.brand_color && /^#[0-9a-fA-F]{3,6}$/.test(clientData.brand_color) ? clientData.brand_color : '#299aa4';

	let html = `
	<!DOCTYPE html>
	<html>
	<head>
		<style>
			body {
				font-family: 'Source Sans Pro', Arial, sans-serif;
				font-size: 12px;
				margin: 0;
				padding: 0;
				color: #243b53;
			}
			.class-section {
				margin-bottom: 30px;
			}
			.class-section.page-break {
				page-break-before: always;
			}
			.summary-card {
				background: #f0f4f8;
				border-radius: 8px;
				padding: 15px;
				margin-bottom: 20px;
			}
			.summary-card h3 {
				margin: 0 0 12px 0;
				font-size: 14px;
				color: #4eb5ac;
				font-weight: bold;
			}
			.summary-grid {
				display: flex;
				justify-content: space-between;
				flex-wrap: wrap;
			}
			.summary-item {
				text-align: center;
				flex: 1;
				min-width: 120px;
				padding: 8px;
			}
			.summary-label {
				font-size: 10px;
				color: #666;
				text-transform: uppercase;
				margin-bottom: 4px;
			}
			.summary-value {
				font-size: 14px;
				font-weight: bold;
				color: #243b53;
			}
			.detail-table {
				width: 100%;
				border-collapse: collapse;
				margin-top: 15px;
				font-size: 11px;
			}
			.detail-table th {
				background-color: ${primaryColor};
				color: white;
				padding: 10px 8px;
				text-align: center;
				font-weight: bold;
				font-size: 10px;
				text-transform: uppercase;
			}
			.detail-table td {
				padding: 8px;
				border-bottom: 1px solid #e2e8f0;
			}
			.detail-table tr:nth-child(even) {
				background-color: #f8fafc;
			}
			.detail-table tr.totals-row {
				background-color: #a6dad5;
				font-weight: bold;
			}
			.text-right { text-align: right; }
			.text-center { text-align: center; }
			.balance-row {
				display: flex;
				justify-content: space-around;
				background: white;
				border: 1px solid #e2e8f0;
				border-radius: 6px;
				padding: 12px;
				margin-bottom: 15px;
			}
			.balance-item {
				text-align: center;
			}
			.balance-label {
				font-size: 9px;
				color: #666;
				text-transform: uppercase;
			}
			.balance-value {
				font-size: 13px;
				font-weight: bold;
				color: #243b53;
			}
			/* Portfolio Summary Styles */
			.portfolio-summary {
				margin-bottom: 30px;
				padding-bottom: 20px;
				border-bottom: 2px solid #4eb5ac;
				page-break-after: always;
			}
			.portfolio-title {
				font-size: 18px;
				font-weight: bold;
				color: #243b53;
				margin-bottom: 25px;
			}
			.portfolio-grid {
				display: flex;
				justify-content: space-between;
				flex-wrap: wrap;
				background: #f0f4f8;
				border-radius: 8px;
				padding: 15px;
				margin-bottom: 20px;
			}
			.portfolio-item {
				text-align: center;
				flex: 1;
				min-width: 140px;
				padding: 10px;
			}
			.portfolio-label {
				font-size: 10px;
				color: #666;
				text-transform: uppercase;
				margin-bottom: 5px;
			}
			.portfolio-value {
				font-size: 16px;
				font-weight: bold;
				color: #243b53;
			}
			.portfolio-value.large {
				font-size: 22px;
				color: #4eb5ac;
			}
			.holdings-grid {
				display: flex;
				flex-wrap: wrap;
				gap: 12px;
				margin-bottom: 20px;
			}
			.holding-card {
				background: white;
				border: 1px solid #e2e8f0;
				border-radius: 8px;
				padding: 12px 16px;
				min-width: 150px;
				flex: 1;
			}
			.holding-class-name {
				font-size: 11px;
				color: #666;
				text-transform: uppercase;
				margin-bottom: 4px;
			}
			.holding-shares {
				font-size: 16px;
				font-weight: bold;
				color: #243b53;
			}
			.chart-section {
				margin-bottom: 20px;
			}
			.chart-title {
				font-size: 14px;
				font-weight: bold;
				color: #243b53;
				margin-bottom: 10px;
			}
			/* Unvested Section Styles */
			.unvested-section {
				page-break-before: always;
				margin-top: 30px;
			}
			.unvested-title {
				font-size: 18px;
				font-weight: bold;
				color: #243b53;
				margin-bottom: 15px;
				padding-bottom: 5px;
				border-bottom: 2px solid #4eb5ac;
			}
			.unvested-summary {
				background: #f0f4f8;
				border-radius: 8px;
				padding: 15px;
				margin-bottom: 20px;
			}
			.unvested-total {
				font-size: 16px;
				font-weight: bold;
				color: #243b53;
			}
			.unvested-total-value {
				font-size: 20px;
				color: #4eb5ac;
				font-weight: bold;
			}
			.unvested-table {
				width: 100%;
				border-collapse: collapse;
				margin-top: 15px;
				font-size: 11px;
			}
			.unvested-table th {
				background-color: #4eb5ac;
				color: white;
				padding: 10px 8px;
				text-align: center;
				font-weight: bold;
				font-size: 10px;
				text-transform: uppercase;
			}
			.unvested-table td {
				padding: 8px;
				border-bottom: 1px solid #e2e8f0;
			}
			.unvested-table tr:nth-child(even) {
				background-color: #f8fafc;
			}
		</style>
	</head>
	<body>
	`;

	// Portfolio Summary Section (before per-class details)
	const classesWithShares = statementData.classes.filter((c) => statementData.byClass[c].totalShares > 0);

	if (classesWithShares.length > 0) {
		html += `
		<div class="portfolio-summary">
			<div class="portfolio-title">Portfolio Summary</div>

			<!-- Holdings Cards -->
			<div class="holdings-grid">
		`;

		classesWithShares.forEach((c) => {
			const classData = statementData.byClass[c];
			html += `
				<div class="holding-card">
					<div class="holding-class-name">${escapeHtml(classData.shareClassName)}</div>
					<div class="holding-shares">${classData.totalShares.toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}${classData.sharesType === 'shares' ? ' shares' : ''}</div>
				</div>
			`;
		});

		html += `
			</div>

			<!-- Summary Grid -->
			<div class="portfolio-grid">
				<div class="portfolio-item">
					<div class="portfolio-label">Statement Date</div>
					<div class="portfolio-value">${statementData.statementDate}</div>
				</div>
				<div class="portfolio-item">
					<div class="portfolio-label">YTD Dividends</div>
					<div class="portfolio-value">$${statementData.totalYtdDividends.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
				</div>
				<div class="portfolio-item">
					<div class="portfolio-label">Total Portfolio Value</div>
					<div class="portfolio-value large">$${statementData.totalPortfolioValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
				</div>
			</div>
		`;

		// Portfolio Value Chart (if time series data available)
		if (statementData.timeSeriesData && statementData.timeSeriesData.length >= 2) {
			// Filter out the "now" point added by TimeSeriesBuilder (show only up to last valuation)
			const oneDayAgo = Date.now() - 24 * 60 * 60 * 1000;
			const chartData = statementData.timeSeriesData.filter((row) => row[0] < oneDayAgo);

			if (chartData.length >= 2) {
				const chartSvg = buildStatementChartSvg(chartData, {
					width: 700,
					height: 180,
					color: '#4eb5ac',
				});
				if (chartSvg) {
					html += `
						<div class="chart-section">
							<div class="chart-title">Portfolio Value Over Time</div>
							<div style="padding: 10px 0;">
								${chartSvg}
							</div>
						</div>
					`;
				}
			}
		}

		html += `
		</div>
		`;
	}

	// Generate section for each share class with shares
	let isFirstClass = true;
	statementData.classes.forEach((shareClass) => {
		const classData = statementData.byClass[shareClass];
		if (!classData.totalShares) return; // Skip classes with no shares

		// Add page break before all classes except the first
		const pageBreakClass = isFirstClass ? '' : 'page-break';
		isFirstClass = false;

		html += `
		<div class="class-section ${pageBreakClass}">
			<!-- SUMMARY CARD -->
			<div class="summary-card">
				<h3>${escapeHtml(classData.shareClassName)}</h3>
				<div class="summary-grid">
					<div class="summary-item">
						<div class="summary-label">Statement Date</div>
						<div class="summary-value">${statementData.statementDate}</div>
					</div>
					<div class="summary-item">
						<div class="summary-label">YTD Dividends</div>
						<div class="summary-value">$${classData.ytdDividends.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
					</div>
					<div class="summary-item">
						<div class="summary-label">Current Price</div>
						<div class="summary-value">$${classData.currentPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
					</div>
					<div class="summary-item">
						<div class="summary-label">Current Value</div>
						<div class="summary-value">$${classData.currentValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
					</div>
				</div>
			</div>

			<!-- BALANCE ROW -->
			<div class="balance-row">
				<div class="balance-item">
					<div class="balance-label">Certificated</div>
					<div class="balance-value">${classData.totalCertificated.toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}</div>
				</div>
				<div class="balance-item">
					<div class="balance-label">Uncertificated</div>
					<div class="balance-value">${classData.totalUncertificated.toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}</div>
				</div>
				<div class="balance-item">
					<div class="balance-label">DRIP</div>
					<div class="balance-value">0.0000</div>
				</div>
				<div class="balance-item">
					<div class="balance-label">Total Shares</div>
					<div class="balance-value">${classData.totalShares.toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}</div>
				</div>
			</div>

			<!-- CERTIFICATE DETAIL TABLE -->
			<table class="detail-table">
				<thead>
					<tr>
						<th>Origination Date</th>
						<th>Current Transfer Date</th>
						<th>Cert # / Book Entry</th>
						<th>Shares / Equity Units</th>
						<th>Gift or Inherited</th>
						<th>G&nbsp;or&nbsp;I Date</th>
						<th>Gift Basis</th>
						<th>Cost Basis</th>
						<th>Basis Per Share</th>
						<th>Cancelled Date</th>
					</tr>
				</thead>
				<tbody>
		`;

		// Add certificated transactions first, then uncertificated
		const allTransactions = [...classData.certificated, ...classData.uncertificated];
		let totalBasis = 0;
		let basisUnknown = false;
		const fmtBasis = (v) => (v == null ? 'n.a.' : v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

		allTransactions.forEach((txn, idx) => {
			if (txn.costBasisCumulative == null) basisUnknown = true;
			else totalBasis += txn.costBasisCumulative;
			html += `
					<tr>
						<td class="text-center">${escapeHtml(txn.originalAcquiredDate)}</td>
						<td class="text-center">${escapeHtml(txn.certificateDate)}</td>
						<td class="text-center">${escapeHtml(txn.certificateNumber)}</td>
						<td class="text-right">${txn.shares.toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}</td>
						<td class="text-center">${txn.giftInherited}</td>
						<td class="text-center">${escapeHtml(txn.giftDate)}</td>
						<td class="text-right">${txn.giftBasis ? txn.giftBasis.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : ''}</td>
						<td class="text-right">${fmtBasis(txn.costBasisCumulative)}</td>
						<td class="text-right">${fmtBasis(txn.costBasisPerShare)}</td>
						<td class="text-center">${escapeHtml(txn.cancelledDate)}</td>
					</tr>
			`;
		});

		// Total row
		// Any unknown row makes the total unknowable, so show n.a. rather than a partial figure.
		const avgBasisPerShare = basisUnknown ? null : classData.totalShares > 0 ? totalBasis / classData.totalShares : 0;
		html += `
					<tr class="totals-row">
						<td colspan="3" class="text-right">${escapeHtml(classData.shareClassName)} Totals:</td>
						<td class="text-right">${classData.totalShares.toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}</td>
						<td></td>
						<td></td>
						<td></td>
						<td class="text-right">${fmtBasis(basisUnknown ? null : totalBasis)}</td>
						<td class="text-right">${fmtBasis(avgBasisPerShare)}</td>
						<td></td>
					</tr>
				</tbody>
			</table>
		</div>
		`;
	});

	// Unvested Data Section (last page)
	if (statementData.unvestedGrants && statementData.unvestedGrants.length > 0) {
		html += `
		<div class="unvested-section">
			<div class="unvested-title">Unvested Equity</div>

			<div class="unvested-summary">
				<div class="unvested-total">
					Total Original (Target) Equity Unit Unvested Value:
					<span class="unvested-total-value">$${statementData.totalUnvestedValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
				</div>
			</div>

			<table class="unvested-table">
				<thead>
					<tr>
						<th>Stock Type</th>
						<th>Equity</th>
						<th>Target Value @ Grant</th>
						<th>Current Price</th>
						<th>Unvested Value</th>
					</tr>
				</thead>
				<tbody>
		`;

		statementData.unvestedGrants.forEach((grant) => {
			html += `
					<tr>
						<td>${escapeHtml(grant.stockType)}</td>
						<td class="text-right">${grant.equity.toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}</td>
						<td class="text-right">$${grant.targetValueAtGrant.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
						<td class="text-right">$${grant.currentPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
						<td class="text-right">$${grant.unvestedValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
					</tr>
			`;
		});

		html += `
				</tbody>
			</table>
		</div>
		`;
	}

	html += `
	</body>
	</html>
	`;

	return html;
}

export function splitBWAddress(address) {
	/* Splits address into BW lines: Street / City, State ZIP / Country */
	const a = splitAddressFields(address);

	const line1 = a.address2 ? `${a.address1}, ${a.address2}` : a.address1;
	const line2 = [a.city, a.state && a.zip ? `${a.state} ${a.zip}` : a.state || a.zip].filter(Boolean).join(', ');
	const line3 = a.country || '';

	return { line1: line1 || address || '', line2: line2 || '', line3 };
}
